import React, { useState } from 'react';
import { Alert } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatCurrency } from '~libs/dataUtils';
import { formatDateString } from '~libs/dayjs';
import { allocationRateOf } from '~support-at-home/lib/budgetFigures';

import { ErrorResponse, SectionCard, Spinner } from '~components';
import ClickableIcon from '~components/ClickableIcon';
import Switch from '~components/form-fields/inputs/Switch';
import { faLink, faLinkSlash, faPenToSquare } from '~components/icon';
import LookupStatusBadge from '~components/LookupStatusBadge';
import { Column, Row, TableView } from '~components/table-view';
import ProgressBar from '~components/visualisation/progress-bar/ProgressBar';

import { CarePlan } from '~clients/models';

import { CARE_PLAN_LINKED_BADGE } from '~clients/config/carePlanEnums';

import { useLinkCarePlanBudgetMutation, useUnlinkCarePlanBudgetMutation } from '~clients/actions/carePlans';
import { useGetBudgetsQuery } from '~support-at-home/actions/budgets';

type LinkState = 'linked-here' | 'linked-elsewhere' | 'unlinked';
interface ComponentProps {
  plan: CarePlan;
}

const budgetRange = (start?: string, end?: string) => `${formatDateString(start)} – ${formatDateString(end)}`;

/**
 * SAH budget linkage. A plan links many client_budgets via a nullable care_plan_id
 * FK. Read mode lists the linked budgets; the header switch reveals all client
 * budgets so more can be linked. Edit opens the budget page in a new tab.
 */
const SahBudgetSection: React.FC<ComponentProps> = ({ plan }) => {
  const { data: budgets, isLoading, error } = useGetBudgetsQuery({ client_id: plan.client_id });
  const [linkBudget, { isLoading: isLinking, error: linkError }] = useLinkCarePlanBudgetMutation();
  const [unlinkBudget, { isLoading: isUnlinking, error: unlinkError }] = useUnlinkCarePlanBudgetMutation();
  // Default to showing all of the client's budgets so linkable ones are visible up-front.
  const [showAll, setShowAll] = useState(true);

  const allBudgets = budgets ?? [];
  const linkedBudgets = allBudgets.filter((budget) => budget.care_plan_id === plan.id);
  const isSaving = isLinking || isUnlinking;
  const saveError = linkError ?? unlinkError;

  const onLink = (budgetId: string) => {
    linkBudget({ planId: plan.id, budgetId });
  };

  const onUnlink = (budgetId: string) => {
    unlinkBudget({ planId: plan.id, budgetId });
  };

  const linkStateFor = (carePlanId?: string | null): LinkState => {
    if (carePlanId === plan.id) return 'linked-here';
    if (carePlanId) return 'linked-elsewhere';
    return 'unlinked';
  };

  const visibleBudgets = showAll ? allBudgets : linkedBudgets;
  const rows: Row[] = visibleBudgets.map((budget) => ({
    id: budget.id,
    reference: budget.reference,
    start_date: budget.start_date,
    end_date: budget.end_date,
    total_amount: budget.total_amount,
    total_allocated: budget.total_allocated,
    available_amount: budget.available_amount,
    care_plan_id: budget.care_plan_id ?? null,
  }));

  // Defined inline (no useMemo) because the actions column closures capture live
  // component state (isSaving, the link/unlink handlers) — memoising would stale them.
  const columns: Column[] = [
    {
      id: 'reference',
      header: 'Budget',
      type: 'custom',
      renderReadOnly: (_value, row) => (
        <span className="care-plan-budget-reference">
          <span className="fw-bold">{row.reference}</span>
          {row.care_plan_id === plan.id && (
            <LookupStatusBadge
              status={CARE_PLAN_LINKED_BADGE}
              dataCy="care-plan-budget-linked-badge"
              className="care-plan-linked-badge"
            />
          )}
        </span>
      ),
    },
    {
      id: 'date_range',
      header: 'Date Range',
      type: 'text',
      deriveValue: (row) => budgetRange(row.start_date, row.end_date),
    },
    {
      id: 'allocation',
      header: 'Allocation',
      type: 'custom',
      renderReadOnly: (_value, row) => {
        // Allocated / Available, the Budget Tracker's Allocation rate (TAV-3343): TurnPoint's own figures, as the
        // /budgets list shows them, not the linked government sah_budget. available_amount is the budget's Available
        // as on its Budget Tracker (including care management and any supplements), not a remaining balance.
        const allocated = row.total_allocated ?? 0;
        const available = row.available_amount ?? 0;
        const percent = Math.round((allocationRateOf(allocated, available) ?? 0) * 100);
        // Own label from unclamped figures so over-allocation reads as >100% (ProgressBar's
        // showLabel would clamp it); whole dollars to match the /budgets list.
        const label = `${formatCurrency(allocated, 0)} / ${formatCurrency(available, 0)} (${percent}%)`;
        return (
          <ProgressBar
            data={{ used: allocated, total: available, units: 'currency' }}
            showLabel
            label={label}
            height={8}
          />
        );
      },
    },
    {
      id: 'actions',
      header: '',
      type: 'custom',
      renderReadOnly: (_value, row) => {
        const linkState = linkStateFor(row.care_plan_id);
        const budgetId = String(row.id);
        return (
          <div className="care-plan-budget-actions">
            {linkState === 'linked-here' && (
              <ClickableIcon
                id={`care-plan-budget-unlink-${budgetId}`}
                dataCy={`care-plan-budget-unlink-${budgetId}`}
                icon={faLinkSlash}
                color="danger"
                disabled={isSaving}
                toolTip="Unlink budget"
                ariaLabel={`Unlink budget ${row.reference}`}
                onClick={() => onUnlink(budgetId)}
              />
            )}
            {linkState === 'unlinked' && (
              <ClickableIcon
                id={`care-plan-budget-link-${budgetId}`}
                dataCy={`care-plan-budget-link-${budgetId}`}
                icon={faLink}
                color="success"
                disabled={isSaving}
                toolTip="Link budget"
                ariaLabel={`Link budget ${row.reference}`}
                onClick={() => onLink(budgetId)}
              />
            )}
            {linkState === 'linked-elsewhere' && (
              <ClickableIcon
                id={`care-plan-budget-link-${budgetId}`}
                dataCy={`care-plan-budget-link-${budgetId}`}
                icon={faLink}
                color="success"
                disabled
                showTooltipWhenDisabled
                toolTip="This budget is linked to another care plan"
                ariaLabel={`Cannot link budget ${row.reference}: linked to another care plan`}
              />
            )}
            <ClickableIcon
              id={`care-plan-budget-edit-${budgetId}`}
              dataCy={`care-plan-budget-edit-${budgetId}`}
              icon={faPenToSquare}
              color="secondary"
              toolTip="Edit budget (opens in new tab)"
              ariaLabel={`Edit budget ${row.reference} (opens in new tab)`}
              onClick={() => window.open(`/support-at-home/budgets/${budgetId}`, '_blank', 'noopener,noreferrer')}
            />
          </div>
        );
      },
    },
  ];

  return (
    <SectionCard
      title="SAH Linked Budgets"
      titleTag="h4"
      className="care-plan-overview-section"
      dataCy="care-plan-sah-budget"
      headerRight={
        allBudgets.length > 0 ? (
          <Switch
            type="switch"
            id="care-plan-budget-show-all"
            name="care-plan-budget-show-all"
            value={showAll}
            labels={['Linked only', 'All budgets']}
            onChange={() => setShowAll((prev) => !prev)}
          />
        ) : undefined
      }
    >
      {isLoading && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {error !== undefined && <ErrorResponse dataCy="care-plan-sah-budget-error" message={getErrorMessage(error)} />}
      {saveError !== undefined && (
        <ErrorResponse dataCy="care-plan-sah-budget-save-error" message={getErrorMessage(saveError)} />
      )}
      {!isLoading && error === undefined && (
        <div className="care-plan-overview-section-body">
          {!showAll && linkedBudgets.length === 0 && allBudgets.length > 0 && (
            <Alert color="warning" className="care-plan-alert" data-cy="care-plan-sah-budget-unlinked">
              No SAH budget is linked to this plan.
            </Alert>
          )}
          {rows.length > 0 && <TableView id="care-plan-budgets" data={rows} columns={columns} readonly bordered />}
          {allBudgets.length === 0 && (
            <Alert color="warning" className="care-plan-alert" data-cy="care-plan-sah-budget-empty">
              No SAH budgets found for this client.
            </Alert>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default SahBudgetSection;
